import { FolderFilled, FolderOutlined, PlusOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, Modal, Select, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"

import { neutral, semantic } from "../../../../components/widgets/tokens.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import {
  canFileReport,
  folderNameProblem
} from "../../helpers/reportFolders.ts"
import { FOLDER_MAX_LENGTH } from "../../helpers/reportSettings.ts"
import { moveReportsToFolder } from "../../redux/reports.thunks.ts"
import type { ReportListItem } from "../../types/types.ts"

const { Text } = Typography

// A Select value cannot be "", which is what Unfiled is on the wire.
const UNFILED_VALUE = "__unfiled__"

interface ReportMoveModalProps {
  reports: ReportListItem[]
  folders: string[]
  onClose: () => void
  /** The ids that moved, so the page can drop them from its selection. */
  onMoved: (ids: string[]) => void
}

/**
 * Moves one or many reports into a folder or to Unfiled. Only the `folder`
 * label is written, report by report, so a refusal on one never undoes the
 * others and nothing about who can see a report changes.
 */
export const ReportMoveModal = ({
  reports,
  folders,
  onClose,
  onMoved
}: ReportMoveModalProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const movable = useMemo(() => reports.filter(canFileReport), [reports])
  const readOnly = reports.length - movable.length
  const sharedFolder =
    reports.length > 0 && reports.every((r) => r.folder === reports[0].folder)
      ? reports[0].folder
      : null

  const [target, setTarget] = useState<string | undefined>(undefined)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState("")
  const [touched, setTouched] = useState(false)
  const [moving, setMoving] = useState(false)
  const [failures, setFailures] = useState<
    { name: string; message: string | null }[]
  >([])

  const newNameProblem = creating ? folderNameProblem(newName, folders) : null
  const destination: string | null = creating
    ? newNameProblem
      ? null
      : newName.trim()
    : target === undefined
      ? null
      : target === UNFILED_VALUE
        ? ""
        : target

  const options = [
    {
      value: UNFILED_VALUE,
      title: t("reports.folders.unfiled"),
      label: (
        <span className="inline-flex items-center gap-2">
          <FolderOutlined style={{ color: neutral.muted }} />
          {t("reports.folders.unfiled")}
        </span>
      ),
      disabled: sharedFolder === ""
    },
    ...folders.map((folder) => ({
      value: folder,
      title: folder,
      label: (
        <span className="inline-flex items-center gap-2">
          <FolderFilled style={{ color: semantic.star }} />
          {folder}
        </span>
      ),
      disabled: sharedFolder === folder
    }))
  ]

  const handleOk = async () => {
    setTouched(true)
    if (destination === null || movable.length === 0 || moving) return
    setMoving(true)
    setFailures([])
    try {
      const action = await dispatch(
        moveReportsToFolder({
          ids: movable.map((report) => report.id),
          folder: destination
        })
      )
      if (!moveReportsToFolder.fulfilled.match(action)) {
        setFailures([{ name: "", message: null }])
        return
      }
      const { moved, failed } = action.payload
      onMoved(moved.map((report) => report.id))
      if (failed.length === 0) {
        onClose()
        return
      }
      const names = new Map(reports.map((report) => [report.id, report.name]))
      setFailures(
        failed.map((failure) => ({
          name: names.get(failure.id) ?? failure.id,
          message: failure.message
        }))
      )
    } finally {
      setMoving(false)
    }
  }

  return (
    <Modal
      open
      centered
      maskClosable={false}
      destroyOnHidden
      width={480}
      title={t("reports.move.title", { count: reports.length })}
      okText={t("reports.move.confirm")}
      cancelText={failures.length ? t("reports.close") : t("reports.cancel")}
      okButtonProps={{ disabled: movable.length === 0 }}
      confirmLoading={moving}
      cancelButtonProps={{ disabled: moving }}
      onOk={handleOk}
      onCancel={() => !moving && onClose()}
    >
      <Flex vertical gap="middle" className="pt-2">
        {failures.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t("reports.move.partialFailure", {
              count: failures.length
            })}
            description={
              <ul className="mb-0 mt-1 max-h-40 list-disc overflow-y-auto pl-5">
                {failures.map((failure, index) => (
                  <li key={`${failure.name}-${index}`}>
                    {failure.name
                      ? t("reports.move.failureLine", {
                          name: failure.name,
                          reason:
                            failure.message || t("reports.move.unknownReason")
                        })
                      : t("reports.move.requestFailed")}
                  </li>
                ))}
              </ul>
            }
          />
        )}

        {readOnly > 0 && (
          <Alert
            type="info"
            showIcon
            message={t("reports.move.readOnlySkipped", { count: readOnly })}
          />
        )}

        <Form layout="vertical" component="div" className="with-default-error">
          {creating ? (
            <Form.Item
              required
              label={t("reports.folders.nameLabel")}
              validateStatus={touched && newNameProblem ? "error" : undefined}
              help={
                touched && newNameProblem
                  ? t(`reports.folders.errors.${newNameProblem}`)
                  : undefined
              }
            >
              <TextInput
                autoFocus
                disableAutoCapitalize
                aria-label={t("reports.folders.nameLabel")}
                value={newName}
                maxLength={FOLDER_MAX_LENGTH}
                placeholder={t("reports.folders.namePlaceholder")}
                onChange={(e) => setNewName(e.target.value)}
                onPressEnter={handleOk}
              />
            </Form.Item>
          ) : (
            <Form.Item
              required
              label={t("reports.move.destination")}
              validateStatus={
                touched && destination === null ? "error" : undefined
              }
              help={
                touched && destination === null
                  ? t("reports.move.destinationRequired")
                  : undefined
              }
            >
              <Select
                showSearch
                value={target}
                onChange={setTarget}
                options={options}
                placeholder={t("reports.move.destinationPlaceholder")}
                aria-label={t("reports.move.destination")}
                filterOption={(input, option) =>
                  option?.value === UNFILED_VALUE
                    ? t("reports.folders.unfiled")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    : String(option?.value ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}
        </Form>

        <div>
          <Button
            type="link"
            size="small"
            className="px-0"
            icon={creating ? undefined : <PlusOutlined aria-hidden />}
            onClick={() => {
              setCreating((value) => !value)
              setTouched(false)
            }}
          >
            {creating
              ? t("reports.move.pickExisting")
              : t("reports.move.intoNewFolder")}
          </Button>
        </div>

        <Text type="secondary" className="text-xs">
          {t("reports.move.accessUnchanged")}
        </Text>
      </Flex>
    </Modal>
  )
}
